// Upload progress + toast manager.
//
// Surfaces a small persistent toast strip in the bottom-right of
// the app showing every in-flight upload and recently-completed
// ones (auto-dismiss after 3s). The actual upload ceremony lives
// in `lib/upload.ts`; this module is just the React-facing shell
// that lets any caller queue an upload and see the progress.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { uploadFile, type UploadInput, type UploadResult } from "../lib/upload";

interface UploadEntry {
  id: string;
  filename: string;
  byteSize: number;
  status: "uploading" | "done" | "error";
  progress: number;
  errorMessage?: string;
}

interface UploadsValue {
  entries: ReadonlyArray<UploadEntry>;
  start: (input: UploadInput) => Promise<UploadResult>;
  dismiss: (id: string) => void;
}

const UploadsContext = createContext<UploadsValue | null>(null);

export function UploadsProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<UploadEntry[]>([]);

  const dismiss = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const start = useCallback(
    async (input: UploadInput): Promise<UploadResult> => {
      const id = `u-${Math.random().toString(36).slice(2, 10)}`;
      setEntries((prev) => [
        ...prev,
        {
          id,
          filename: input.file.name,
          byteSize: input.file.size,
          status: "uploading",
          progress: 0,
        },
      ]);
      try {
        const result = await uploadFile({
          ...input,
          onProgress: (fraction) => {
            setEntries((prev) =>
              prev.map((e) =>
                e.id === id ? { ...e, progress: fraction } : e,
              ),
            );
            input.onProgress?.(fraction);
          },
        });
        setEntries((prev) =>
          prev.map((e) =>
            e.id === id ? { ...e, status: "done", progress: 1 } : e,
          ),
        );
        // Auto-dismiss after 3s on success.
        window.setTimeout(() => dismiss(id), 3_000);
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setEntries((prev) =>
          prev.map((e) =>
            e.id === id ? { ...e, status: "error", errorMessage: msg } : e,
          ),
        );
        throw err;
      }
    },
    [dismiss],
  );

  const value = useMemo<UploadsValue>(
    () => ({ entries, start, dismiss }),
    [entries, start, dismiss],
  );

  return (
    <UploadsContext.Provider value={value}>{children}</UploadsContext.Provider>
  );
}

export function useUploads(): UploadsValue {
  const ctx = useContext(UploadsContext);
  if (ctx === null) {
    throw new Error("useUploads must be used inside <UploadsProvider>");
  }
  return ctx;
}
